"use client";

import { useEffect, useState } from "react";
import { Link } from "@/components/common/Link";
import { WvcLogo } from '@/integrations/wordpress/WvcLogo';
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Menu, X } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
function HeaderMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden md:flex items-center gap-8">
        {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-4 w-16 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu className="hidden md:flex" viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="text-sm font-medium text-foreground/70 hover:text-primary transition-colors duration-200">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const {
    menuItems,
    loading
  } = useMenu();
  return <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <button className="p-2 text-foreground/70 hover:text-primary transition-colors" aria-label="Avaa valikko">
          <Menu className="h-6 w-6" />
        </button>
      </SheetTrigger>
      <SheetContent side="left" className="w-full sm:w-[300px] p-0 border-r border-border bg-background">
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-6 border-b border-border">
            <SheetTitle className="text-left">
              <WvcLogo className="h-6 w-auto" />
            </SheetTitle>
            <button onClick={() => setIsOpen(false)} className="p-2 text-foreground/70 hover:text-primary transition-colors">
              <X className="h-6 w-6" />
            </button>
          </div>
          
          <nav className="flex-1 overflow-y-auto py-8 px-6">
            {loading ? <div className="space-y-6">
                {[1, 2, 3, 4, 5].map(i => <div key={i} data-index={i} className="h-6 w-32 bg-muted animate-pulse rounded-md" />)}
              </div> : <ul className="space-y-6">
                {menuItems.map((item, i) => <li key={item.id} data-index={i}>
                    <Link to={item.href || "#"} onClick={() => setIsOpen(false)} className="text-lg font-medium text-foreground/70 hover:text-primary transition-colors block">
                      {item.label}
                    </Link>
                  </li>)}
              </ul>}
          </nav>
        </div>
      </SheetContent>
    </Sheet>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            const targetY = element.getBoundingClientRect().top + window.scrollY - 80;
            window.scrollTo({
              top: targetY,
              behavior: "smooth"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="ylätunniste" className={`sticky top-0 z-50 w-full transition-all duration-300 ${isScrolled ? "bg-background/95 backdrop-blur-md border-b border-border shadow-sm" : "bg-background"}`}>
      <MenuProvider menu_id="49" className="container mx-auto px-6">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center">
            <WvcLogo className="h-7 w-auto" />
          </Link>

          <HeaderMenu />

          <div className="md:hidden">
            <MobileMenu />
          </div>
        </div>
      </MenuProvider>
    </header>;
}